'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('sent');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  }

  return (
    <AppLayout>
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
              Get in touch
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>
              Contact Us
            </h1>
            <p className="text-base max-w-xl" style={{ color: 'var(--muted-foreground)' }}>
              Have a question, partnership inquiry, or want to contribute to the archive? We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Contact info */}
            <div className="lg:col-span-2 space-y-6">
              <div
                className="rounded-xl p-6 border"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <h2 className="text-sm font-semibold mb-5" style={{ color: 'var(--foreground)' }}>
                  Reach us directly
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--secondary)' }}>
                      <MapPin size={15} style={{ color: 'var(--primary)' }} />
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase tracking-wider mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>Address</p>
                      <p className="text-sm" style={{ color: 'var(--foreground)' }}>8710 Fairview<br />Ruwa, Zimbabwe</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--secondary)' }}>
                      <Phone size={15} style={{ color: 'var(--primary)' }} />
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase tracking-wider mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>Phone</p>
                      <a href="tel:+263779604727" className="text-sm hover:underline" style={{ color: 'var(--foreground)' }}>
                        +263 779 604 727
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--secondary)' }}>
                      <Mail size={15} style={{ color: 'var(--primary)' }} />
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase tracking-wider mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>Email</p>
                      <a href="mailto:hello@griott.org" className="text-sm hover:underline" style={{ color: 'var(--foreground)' }}>
                        hello@griott.org
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="rounded-xl p-6 border"
                style={{ backgroundColor: 'var(--secondary)', borderColor: 'var(--border)' }}
              >
                <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>Response time</p>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  We typically respond within <strong style={{ color: 'var(--foreground)' }}>1–2 business days</strong>. For urgent matters, call us directly.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div
                className="rounded-xl border p-8"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
              >
                {status === 'sent' ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                    <CheckCircle size={40} style={{ color: 'var(--status-published)' }} />
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Message sent!</h3>
                    <p className="text-sm max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
                      Thank you for reaching out. We'll get back to you within 1–2 business days.
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-2 text-sm font-medium hover:underline"
                      style={{ color: 'var(--primary)' }}
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                          Your name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Tendai Moyo"
                          className="w-full rounded-lg px-3.5 py-2.5 text-sm border outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: 'var(--input)',
                            borderColor: 'var(--border)',
                            color: 'var(--foreground)',
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                          Email address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          className="w-full rounded-lg px-3.5 py-2.5 text-sm border outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: 'var(--input)',
                            borderColor: 'var(--border)',
                            color: 'var(--foreground)',
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                        Subject *
                      </label>
                      <select
                        name="subject"
                        required
                        value={form.subject}
                        onChange={handleChange}
                        className="w-full rounded-lg px-3.5 py-2.5 text-sm border outline-none focus:ring-2 transition-all"
                        style={{
                          backgroundColor: 'var(--input)',
                          borderColor: 'var(--border)',
                          color: form.subject ? 'var(--foreground)' : 'var(--muted-foreground)',
                        }}
                      >
                        <option value="">Select a subject…</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Partnership Opportunity">Partnership Opportunity</option>
                        <option value="Contribution Support">Contribution Support</option>
                        <option value="Technical Issue">Technical Issue</option>
                        <option value="Media & Press">Media & Press</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider mb-1.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                        Message *
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={6}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us what's on your mind…"
                        className="w-full rounded-lg px-3.5 py-2.5 text-sm border outline-none focus:ring-2 transition-all resize-none"
                        style={{
                          backgroundColor: 'var(--input)',
                          borderColor: 'var(--border)',
                          color: 'var(--foreground)',
                        }}
                      />
                    </div>

                    {status === 'error' && (
                      <p className="text-sm" style={{ color: 'var(--status-rejected)' }}>
                        Something went wrong. Please try again or email us directly.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-60"
                      style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                    >
                      <Send size={15} />
                      {status === 'sending' ? 'Sending…' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
